import { Loader2 } from "lucide-react";
import type { InvoiceAgingBucket } from "@adva/types";
import {
  useInvoiceAgingSummary,
  type InvoiceAgingSummaryRow,
} from "@/hooks/use-api";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/v2/empty-state";
import { formatCurrency } from "@/lib/utils";
import { cn } from "@/lib/utils";

interface InvoicesAgingViewProps {
  onBucketSelect: (bucket: InvoiceAgingBucket) => void;
}

interface BucketDef {
  bucket: InvoiceAgingBucket;
  label: string;
  isDanger: boolean;
}

// Order matches the AR aging convention (Current → most overdue).
const BUCKETS: BucketDef[] = [
  { bucket: "current", label: "Current", isDanger: false },
  { bucket: "days_31_60", label: "31 – 60 days", isDanger: false },
  { bucket: "days_61_90", label: "61 – 90 days", isDanger: false },
  // Per ADR-0018: semantic colors live on data, never chrome.
  // The 90+ tile uses --color-danger on the value text only.
  { bucket: "days_90_plus", label: "90+ days", isDanger: true },
];

function findRow(
  rows: InvoiceAgingSummaryRow[] | undefined,
  bucket: InvoiceAgingBucket,
): InvoiceAgingSummaryRow {
  return (
    rows?.find((r) => r.bucket === bucket) ?? {
      bucket,
      invoice_count: 0,
      total_balance: 0,
    }
  );
}

export function InvoicesAgingView({ onBucketSelect }: InvoicesAgingViewProps) {
  const { data, isLoading, isError, error } = useInvoiceAgingSummary();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" role="status">
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--color-text-lo)]" />
        <span className="sr-only">Loading aging summary</span>
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="p-4 text-[color:var(--color-danger)]">
        <p className="typo-body">
          Couldn&apos;t load AR aging summary.
          {error instanceof Error ? ` ${error.message}` : ""}
        </p>
      </Card>
    );
  }

  const buckets = data?.buckets ?? [];
  // TODO(ADV-1335): these reduces are a known database-first violation —
  // replaced by finance.v_invoice_aging_total in ADV-1335.
  const totalCount = buckets.reduce((s, b) => s + b.invoice_count, 0);
  const totalBalance = buckets.reduce((s, b) => s + b.total_balance, 0);

  if (totalCount === 0) {
    return (
      <EmptyState
        title="No outstanding invoices"
        description="Invoices appear here once they are sent and have a balance due."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        role="list"
        aria-label="AR aging buckets"
      >
        {BUCKETS.map((def) => {
          const row = findRow(buckets, def.bucket);
          return (
            <button
              key={def.bucket}
              type="button"
              role="listitem"
              onClick={() => onBucketSelect(def.bucket)}
              aria-label={`Filter table to ${def.label}: ${row.invoice_count} invoices, ${formatCurrency(row.total_balance)} outstanding`}
              className="text-left transition-shadow rounded-lg hover:ring-1 hover:ring-[color:var(--color-border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
            >
              <Card className="flex h-[120px] flex-col justify-center p-4">
                <div
                  className={cn(
                    "text-[length:var(--font-size-heading)] font-semibold tabular-nums",
                    def.isDanger
                      ? "text-[color:var(--color-danger)]"
                      : "text-[color:var(--color-text-hi)]",
                  )}
                >
                  {row.invoice_count.toLocaleString()}
                </div>
                <div className="typo-label mt-1 text-[length:var(--font-size-label)] uppercase tracking-[0.08em] text-[color:var(--color-text-md)]">
                  {def.label}
                </div>
                <div className="mt-2 text-[length:var(--font-size-label)] tabular-nums text-[color:var(--color-text-md)]">
                  {formatCurrency(row.total_balance)} outstanding
                </div>
              </Card>
            </button>
          );
        })}
      </div>
      <div
        className="flex items-center justify-between rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-elevated)] px-4 py-2"
        aria-label="AR aging totals"
      >
        <span className="typo-label uppercase tracking-[0.08em] text-[color:var(--color-text-md)]">
          Total outstanding
        </span>
        <span className="typo-mono tabular-nums text-[color:var(--color-text-hi)]">
          {totalCount.toLocaleString()} invoices ·{" "}
          {formatCurrency(totalBalance)}
        </span>
      </div>
    </div>
  );
}
